import { ReactNode, useState } from 'react';
import { Box, Button, Popover, Typography } from '@mui/material';
import { MultiselectSearchableField } from 'components/Multiselect';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { ColumnPreferencesConfig } from '@common/types';

type ColumnSelection<TColumnKey extends string> = Partial<
  Record<TColumnKey, ColumnPreferencesConfig>
>;

type ColumnSelectionFilterProps<TColumnKey extends string> = {
  name: string;
  label?: string;
  columns: ColumnSelection<TColumnKey>;
  columnLabels: Record<TColumnKey, string>;
  onChange: (selection: ColumnSelection<TColumnKey>) => void;
  optional?: boolean;
  disabled?: boolean;
  loading?: boolean;
  footerAction?: ReactNode;
  hideText?: boolean;
};

export const ColumnSelectionFilter = <TColumnKey extends string>({
  name,
  label = 'Columns',
  columns,
  columnLabels,
  onChange,
  optional = true,
  disabled = false,
  loading = false,
  footerAction,
  hideText,
}: ColumnSelectionFilterProps<TColumnKey>) => {
  const options = (Object.keys(columnLabels) as TColumnKey[]).map(key => ({
    value: key,
    label: columnLabels[key],
  }));

  const selectedValues = (
    Object.entries(columns) as [
      TColumnKey,
      ColumnPreferencesConfig | undefined,
    ][]
  )
    .filter(
      ([, config]) => config != null && config.column_display !== 'hidden',
    )
    .sort(([, a], [, b]) => (a?.column_order ?? 0) - (b?.column_order ?? 0))
    .map(([key]) => key);

  const [allSelected, setAllSelected] = useState<boolean>(false);

  const handleSelectionChange = (values: string[]) => {
    setAllSelected(values.length === 0 ? true : false);

    const nextSelection = Object.fromEntries(
      Object.keys(columns).map((value, index) => {
        const key = value as TColumnKey;
        const existing = columns[key];

        return [
          key,
          {
            column_display:
              values.includes(key) || values.length === 0
                ? 'display'
                : 'hidden',
            column_size: existing?.column_size ?? 120,
            column_order: index,
          },
        ];
      }),
    ) as ColumnSelection<TColumnKey>;

    onChange(nextSelection);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };
  const selectedColumnsCount =
    selectedValues.length === options.length || allSelected
      ? 0
      : selectedValues.length;

  return (
    <>
      <Box
        sx={{
          bgcolor: anchorEl ? 'rgba(19, 63, 110, 0.3)' : 'unset',
        }}
        onClick={handleOpen}
      >
        <Button
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            fontSize: '15px',
            height: '100%',
            fontWeight: 'medium',
          }}
          color='primary'
          variant='text'
        >
          <ViewColumnIcon
            color={disabled ? 'disabled' : 'primary'}
            sx={{ height: '24px' }}
          />
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 'medium',
              display: {
                lg: 'initial',
                md: hideText ? 'none' : 'initial',
                sm: 'none',
              },
              paddingX: { sm: 'none', md: hideText ? '0' : 1, lg: 1 },
            }}
          >
            COLUMNS
          </Typography>
          <Typography sx={{ fontSize: '15px', fontWeight: 'medium' }}>
            {selectedColumnsCount > 0 ? `(${selectedColumnsCount})` : ''}
          </Typography>
        </Button>
      </Box>
      <Popover
        id={`${name}-popover`}
        open={!!anchorEl}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1,
          width: '100%',
          height: '100%',
        }}
      >
        <Box sx={{ p: '1rem' }}>
          <MultiselectSearchableField
            options={options}
            value={allSelected ? [] : selectedValues}
            name={name}
            label={label}
            handleChange={handleSelectionChange}
            optional={optional}
            disabled={disabled}
            clearable
            sx={{
              backgroundColor: theme => theme.palette.common.white,
              minWidth: '236px',
            }}
            loading={loading}
            minSelectedOptionsCount={4}
            showSelectedCount
            selectedCountOverride={
              allSelected ? options.length : selectedValues.length
            }
            paperFooter={footerAction}
          />
        </Box>
      </Popover>
    </>
  );
};
